import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useAiFeature } from "@/lib/ai";

/** Something to do once the panel is open: ask a question or run a saved report */
export interface AskRequest {
  id: string;
  q?: string;
  report?: string;
}
/** The panel sits beside the page only on screens this wide; narrower, it floats over the page so the page keeps its width */
export const ASK_DOCK_QUERY = "(min-width: 1600px)";

interface AskPanelApi {
  open: boolean;
  /** Open and beside the page (wide screens), so the page has less room */
  docked: boolean;
  /** Ask AI can be used: questions, or the assistant that makes changes */
  available: boolean;
  openAsk: (r?: { q?: string; report?: string }) => void;
  closeAsk: () => void;
  toggleAsk: () => void;
  request: AskRequest | null;
  doneRequest: () => void;
}

const Ctx = createContext<AskPanelApi>({ open: false, docked: false, available: false, openAsk: () => undefined, closeAsk: () => undefined, toggleAsk: () => undefined, request: null, doneRequest: () => undefined });
export const useAskPanel = () => useContext(Ctx);

const OPEN_KEY = "ask.panelOpen";

/** Ask AI as a panel beside every page: the top bar, Ctrl K, Ctrl J and old /ask links all open it */
export function AskPanelProvider({ children }: { children: React.ReactNode }) {
  const askOn = useAiFeature("ASK_CRM");
  const agentFeature = useAiFeature("AGENT");
  const { hasPermission } = useAuth();
  const available = askOn || (agentFeature && hasPermission("ai_agent.use"));
  const [open, setOpen] = useState(() => {
    try {
      return sessionStorage.getItem(OPEN_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [request, setRequest] = useState<AskRequest | null>(null);
  const [wide, setWide] = useState(() => typeof window !== "undefined" && window.matchMedia(ASK_DOCK_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(ASK_DOCK_QUERY);
    const on = () => setWide(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(OPEN_KEY, open ? "1" : "0");
    } catch {
      /* not remembered; fine */
    }
  }, [open]);

  const openAsk = useCallback((r?: { q?: string; report?: string }) => {
    setOpen(true);
    if (r?.q || r?.report) setRequest({ id: Math.random().toString(36).slice(2, 10), ...r });
  }, []);
  const closeAsk = useCallback(() => setOpen(false), []);
  const toggleAsk = useCallback(() => setOpen((o) => !o), []);
  const doneRequest = useCallback(() => setRequest(null), []);

  // Ctrl J (or ⌘J) opens and closes the panel from anywhere
  useEffect(() => {
    if (!available) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "j") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [available]);

  const value = useMemo(
    () => ({ open: open && available, docked: open && available && wide, available, openAsk, closeAsk, toggleAsk, request, doneRequest }),
    [open, available, wide, openAsk, closeAsk, toggleAsk, request, doneRequest],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
